import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  BiometricAuth,
  BiometryType,
  CheckBiometryResult,
} from '@aparajita/capacitor-biometric-auth';

const BIOMETRIC_ENABLED_KEY = 'bfa_biometric_enabled';
const BIOMETRIC_PHONE_KEY = 'bfa_biometric_phone';
const BIOMETRIC_PIN_KEY = 'bfa_biometric_pin';

export interface BiometricStatus {
  isAvailable: boolean;
  isEnabled: boolean;
  biometryType: BiometryType;
  biometryName: string;
}

class BiometricService {
  private biometryResult: CheckBiometryResult | null = null;

  /**
   * Check if biometric authentication is available on this device
   */
  async checkAvailability(): Promise<CheckBiometryResult> {
    if (!Capacitor.isNativePlatform()) {
      return {
        isAvailable: false,
        strongBiometryIsAvailable: false,
        biometryType: BiometryType.none,
        biometryTypes: [],
        deviceIsSecure: false,
        reason: 'Biometrics only available on native platforms',
        code: undefined,
        strongReason: '',
        strongCode: undefined,
      };
    }

    try {
      this.biometryResult = await BiometricAuth.checkBiometry();
      return this.biometryResult;
    } catch (error) {
      console.error('Error checking biometry:', error);
      return {
        isAvailable: false,
        strongBiometryIsAvailable: false,
        biometryType: BiometryType.none,
        biometryTypes: [],
        deviceIsSecure: false,
        reason: 'Error checking biometry',
        code: undefined,
        strongReason: '',
        strongCode: undefined,
      };
    }
  }

  /**
   * Get a human-readable name for the biometry type
   */
  getBiometryName(type: BiometryType): string {
    switch (type) {
      case BiometryType.faceId:
        return 'Face ID';
      case BiometryType.touchId:
        return 'Touch ID';
      case BiometryType.faceAuthentication:
        return 'Face Unlock';
      case BiometryType.fingerprintAuthentication:
        return 'Fingerprint';
      case BiometryType.irisAuthentication:
        return 'Iris';
      default:
        return 'Biometrics';
    }
  }

  /**
   * Get the current biometric status
   */
  async getStatus(): Promise<BiometricStatus> {
    const result = await this.checkAvailability();
    const isEnabled = await this.isEnabled();

    return {
      isAvailable: result.isAvailable,
      isEnabled,
      biometryType: result.biometryType,
      biometryName: this.getBiometryName(result.biometryType),
    };
  }

  /**
   * Check if biometric login is enabled for this user
   * Uses secure Capacitor Preferences storage
   */
  async isEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    return value === 'true';
  }

  /**
   * Get stored phone number for biometric login
   * Uses secure Capacitor Preferences storage
   */
  async getStoredPhone(): Promise<string | null> {
    const { value } = await Preferences.get({ key: BIOMETRIC_PHONE_KEY });
    return value;
  }

  /**
   * Enable biometric authentication and store credentials securely
   * Uses Capacitor Preferences (Keychain on iOS, EncryptedSharedPreferences on Android)
   */
  async enable(phone: string, pin: string): Promise<boolean> {
    try {
      const result = await this.checkAvailability();
      if (!result.isAvailable) {
        console.error('Biometrics not available');
        return false;
      }

      // Authenticate to confirm user identity before storing
      const authenticated = await this.authenticate(
        'Confirm your identity to enable ' + this.getBiometryName(result.biometryType)
      );

      if (!authenticated) {
        return false;
      }

      // Store credentials securely using Capacitor Preferences
      await Preferences.set({ key: BIOMETRIC_PHONE_KEY, value: phone });
      await Preferences.set({ key: BIOMETRIC_PIN_KEY, value: pin });
      await Preferences.set({ key: BIOMETRIC_ENABLED_KEY, value: 'true' });

      return true;
    } catch (error) {
      console.error('Error enabling biometrics:', error);
      return false;
    }
  }

  /**
   * Disable biometric authentication and clear stored credentials
   */
  async disable(): Promise<void> {
    await Preferences.remove({ key: BIOMETRIC_ENABLED_KEY });
    await Preferences.remove({ key: BIOMETRIC_PHONE_KEY });
    await Preferences.remove({ key: BIOMETRIC_PIN_KEY });
  }

  /**
   * Authenticate using biometrics
   */
  async authenticate(reason?: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      await BiometricAuth.authenticate({
        reason: reason || 'Sign in to Bradanna Field',
        cancelTitle: 'Use PIN',
        allowDeviceCredential: false,
        iosFallbackTitle: 'Use PIN',
        androidTitle: 'Bradanna Field',
        androidSubtitle: 'Sign in with biometrics',
      });

      return true;
    } catch (error: any) {
      // User cancelled or biometrics failed
      console.log('Biometric authentication failed or cancelled:', error);
      return false;
    }
  }

  /**
   * Attempt biometric login - returns credentials if successful
   * Uses secure Capacitor Preferences storage
   */
  async biometricLogin(): Promise<{ phone: string; pin: string } | null> {
    const enabled = await this.isEnabled();
    if (!enabled) {
      return null;
    }

    const { value: phone } = await Preferences.get({ key: BIOMETRIC_PHONE_KEY });
    const { value: pin } = await Preferences.get({ key: BIOMETRIC_PIN_KEY });

    if (!phone || !pin) {
      await this.disable();
      return null;
    }

    const authenticated = await this.authenticate();
    if (!authenticated) {
      return null;
    }

    return { phone, pin };
  }

  /**
   * Clear biometric data on logout
   */
  clearOnLogout(): void {
    // Optionally keep biometric enabled for the same user
    // For security, you might want to call disable() here instead
  }
}

export const biometricService = new BiometricService();
